import { useState } from "react";
import axios from "axios";
import { MapPin, ExternalLink, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { nom: "", email: "", telephone: "", message: "" };

export default function NotreAdresse() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status.state === "loading") return;

    if (!form.nom.trim() || !form.email.trim() || !form.telephone.trim() || !form.message.trim()) {
      setStatus({ state: "error", error: "Merci de remplir tous les champs." });
      return;
    }

    setStatus({ state: "loading", error: "" });
    try {
      await axios.post(`${API}/contacts`, {
        nom: form.nom.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
        message: form.message.trim(),
      });
      setStatus({ state: "success", error: "" });
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        error: typeof detail === "string" ? detail : "Une erreur est survenue. Réessayez dans un instant.",
      });
    }
  };

  return (
    <section
      id="adresse"
      data-testid="adresse-section"
      className="relative py-12 sm:py-16 bg-white overflow-hidden"
    >
      {/* Subtle Google-yellow ambient corner (mirror of Avis section) */}
      <div
        className="absolute -bottom-24 -right-32 h-72 w-72 rounded-full blur-3xl opacity-15"
        style={{ background: "#FBBC05" }}
        aria-hidden="true"
      />

      <div className="bd-container relative">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {/* Left: Contact form */}
          <div
            data-testid="contact-form-card"
            className="bg-white rounded-2xl border border-[#e6edf3] shadow-[0_20px_40px_-24px_rgba(35,124,181,0.25)] p-5 sm:p-6 flex flex-col"
          >
            <span className="bd-eyebrow">Une question ?</span>
            <h2 className="mt-2 text-xl sm:text-2xl font-extrabold text-[#0f1d2b] leading-tight">
              Écris-nous, on répond vite.
            </h2>
            <p className="mt-1.5 text-[13px] text-[#4a5b6c]">
              Une question rapide, un conseil, un créneau&nbsp;? Laissez-nous un mot, on revient vers
              vous dans la journée.
            </p>

            <form onSubmit={onSubmit} className="mt-4 space-y-3 flex-1 flex flex-col" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="contact-nom" className="text-[#0f1d2b] text-sm font-semibold">
                    Nom
                  </Label>
                  <Input
                    id="contact-nom"
                    name="nom"
                    type="text"
                    value={form.nom}
                    onChange={onChange}
                    placeholder="Jean Dupont"
                    autoComplete="name"
                    required
                    data-testid="contact-input-nom"
                    className="mt-1.5 bg-white border-[#e6edf3] focus-visible:ring-[#237cb5]"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-tel" className="text-[#0f1d2b] text-sm font-semibold">
                    Téléphone
                  </Label>
                  <Input
                    id="contact-tel"
                    name="telephone"
                    type="tel"
                    value={form.telephone}
                    onChange={onChange}
                    placeholder="06 12 34 56 78"
                    autoComplete="tel"
                    required
                    data-testid="contact-input-telephone"
                    className="mt-1.5 bg-white border-[#e6edf3] focus-visible:ring-[#237cb5]"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contact-email" className="text-[#0f1d2b] text-sm font-semibold">
                  Email
                </Label>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="vous@exemple.com"
                  autoComplete="email"
                  required
                  data-testid="contact-input-email"
                  className="mt-1.5 bg-white border-[#e6edf3] focus-visible:ring-[#237cb5]"
                />
              </div>

              <div className="flex-1 flex flex-col">
                <Label htmlFor="contact-message" className="text-[#0f1d2b] text-sm font-semibold">
                  Message
                </Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Votre question…"
                  rows={3}
                  required
                  data-testid="contact-input-message"
                  className="mt-1.5 bg-white border-[#e6edf3] focus-visible:ring-[#237cb5] resize-none flex-1 min-h-[80px]"
                />
              </div>

              {status.state === "error" && (
                <div
                  data-testid="contact-error"
                  className="flex items-start gap-2 text-sm text-[#b42318] bg-[#fef3f2] border border-[#fecdca] rounded-lg px-3 py-2"
                >
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{status.error}</span>
                </div>
              )}

              {status.state === "success" && (
                <div
                  data-testid="contact-success"
                  className="flex items-start gap-2 text-sm text-[#067647] bg-[#ecfdf3] border border-[#abefc6] rounded-lg px-3 py-2"
                >
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Message envoyé&nbsp;! On revient vers vous très vite.</span>
                </div>
              )}

              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid="contact-submit-button"
                className="mt-1 h-12 inline-flex items-center justify-center gap-2 self-start rounded-xl px-6 bg-[#FF7A00] hover:bg-[#E66E00] disabled:opacity-60 disabled:cursor-not-allowed text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)] transition-colors whitespace-nowrap"
              >
                {status.state === "loading" ? "Envoi…" : "Envoyer"}
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>

          {/* Right: Address + small map */}
          <div className="flex flex-col">
            <span className="bd-eyebrow">Nous trouver</span>
            <h3 className="mt-2 text-xl sm:text-2xl font-extrabold text-[#0f1d2b] flex items-start gap-2 leading-tight">
              <MapPin className="h-5 w-5 text-[#237cb5] shrink-0 mt-1" aria-hidden="true" />
              <span>17 Rue Henry Monnier, 75009 Paris</span>
            </h3>
            <a
              href="https://www.google.com/maps/place/17+Rue+Henry+Monnier,+75009+Paris,+France"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="adresse-maps-link"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#237cb5] hover:text-[#1c6699] transition-colors w-fit"
            >
              Voir sur Google Maps
              <ExternalLink className="h-3.5 w-3.5" />
            </a>

            <div className="mt-4 flex-1 min-h-[220px] rounded-2xl overflow-hidden border border-[#e6edf3] shadow-[0_20px_40px_-24px_rgba(35,124,181,0.25)]">
              <iframe
                title="Big Déménagement — 17 Rue Henry Monnier, 75009 Paris"
                src="https://www.google.com/maps?q=17+Rue+Henry+Monnier,+75009+Paris,+France&hl=fr&z=16&output=embed"
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="block w-full h-full border-0 min-h-[220px]"
                allowFullScreen={false}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
